import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Key, Plus, Copy, Trash2, Loader2, CheckCircle, Eye, EyeOff,
  Code, Shield, Activity, Database, Users, FileText, BarChart3,
  Globe, ShoppingBag, Megaphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminApiKeys() {
  const { getAuthHeader } = useAuth();
  const [keys, setKeys] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState("Master Admin Key");
  const [newKey, setNewKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchKeys();
    fetchStats();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: getAuthHeader() });
      setKeys(res.data?.keys || []);
      setAvailableScopes(res.data?.available_scopes || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const createKey = async () => {
    if (!keyName.trim()) { toast.error("Key name is required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { name: keyName, scopes: [] }, { headers: getAuthHeader() });
      setNewKey(res.data.key);
      toast.success("Admin API key created with all scopes!");
      setKeyName("Master Admin Key");
      await fetchKeys();
    } catch (err) { toast.error("Failed to create API key"); }
    finally { setCreating(false); }
  };

  const revokeKey = async (keyId) => {
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: getAuthHeader() });
      toast.success("API key revoked");
      await fetchKeys();
    } catch (err) { toast.error("Failed to revoke key"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-api-keys">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#F59E0B]" /> Admin API
          </h1>
          <p className="text-[#94A3B8] mt-1">Full directory access with master API keys — all scopes enabled</p>
        </div>
      </div>

      {/* Directory Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Businesses", value: stats.businesses?.total || 0, icon: Globe, color: "#7C3AED" },
            { label: "Users", value: stats.users?.total || 0, icon: Users, color: "#10B981" },
            { label: "Marketplace", value: stats.marketplace?.total || 0, icon: ShoppingBag, color: "#F59E0B" },
            { label: "Content", value: stats.content?.total || 0, icon: FileText, color: "#EC4899" },
          ].map((s, i) => (
            <div key={i} className="bg-[#0F172A] border border-white/10 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}20` }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-[#64748B]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Key Created Banner */}
      {newKey && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-[#10B981]" />
            <h3 className="text-white font-semibold">Admin API Key Created — Save This Now!</h3>
          </div>
          <div className="flex items-center gap-2 bg-black/30 rounded-lg p-3">
            <code className="text-[#10B981] text-sm flex-1 font-mono break-all">{newKey}</code>
            <button onClick={() => copyToClipboard(newKey)} className="text-white hover:text-[#10B981] p-2"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-[#94A3B8] text-xs mt-2">This key has ALL scopes and full directory access. It will not be shown again.</p>
          <button onClick={() => setNewKey(null)} className="text-[#64748B] text-xs mt-2 hover:text-white">Dismiss</button>
        </motion.div>
      )}

      {/* Create Key */}
      <div className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2"><Key className="w-5 h-5 text-[#F59E0B]" /> Generate Admin API Key</h2>
        <div className="flex gap-3">
          <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Key name" className="bg-white/5 border-white/10 text-white max-w-xs" data-testid="admin-key-name" />
          <Button onClick={createKey} disabled={creating} className="bg-[#F59E0B] hover:bg-[#FBBF24] text-black" data-testid="create-admin-key-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Generate All-Scopes Key
          </Button>
        </div>
        <p className="text-[#64748B] text-xs mt-2">Admin keys have full access to all directory endpoints: businesses, users, leads, marketplace, content, analytics.</p>
      </div>

      {/* Existing Keys */}
      {keys.length > 0 && (
        <div className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">Active Admin Keys</h2>
          <div className="space-y-3">
            {keys.map((k, i) => (
              <div key={k.key_id || i} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3">
                  <Key className="w-4 h-4 text-[#F59E0B]" />
                  <div>
                    <p className="text-white font-medium text-sm">{k.name}</p>
                    <p className="text-[#64748B] text-xs font-mono">{k.key_prefix}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[#64748B] text-xs flex items-center gap-1"><Activity className="w-3 h-3" />{k.usage_count || 0} calls</span>
                  <span className="text-[#64748B] text-xs">{k.last_used_at ? `Last: ${new Date(k.last_used_at).toLocaleDateString()}` : "Never used"}</span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium text-[#10B981] bg-[#10B981]/10">{k.scopes?.length || 0} scopes</span>
                  <button onClick={() => revokeKey(k.key_id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#64748B] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* API Documentation */}
      <div className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <button onClick={() => setShowDocs(!showDocs)} className="flex items-center gap-2 text-white font-semibold w-full text-left" data-testid="toggle-admin-docs">
          <Code className="w-5 h-5 text-[#7C3AED]" />
          Admin API Documentation
          <span className="ml-auto text-[#64748B] text-sm">{showDocs ? "Hide" : "Show"}</span>
        </button>

        {showDocs && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 space-y-4">
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-[#94A3B8] text-sm mb-3">Base URL: <code className="text-[#00E5FF]">{BASE_URL}/api/v1/admin/public</code></p>
              <p className="text-[#94A3B8] text-sm mb-4">Auth: <code className="text-[#F59E0B]">X-API-Key: your_admin_key</code></p>
            </div>

            {[
              { method: "GET", path: "/businesses", desc: "List all businesses (paginated). Query: search, category, page, limit", scope: "businesses:read" },
              { method: "GET", path: "/businesses/{slug}", desc: "Get a specific business by slug", scope: "businesses:read" },
              { method: "PATCH", path: "/businesses/{slug}", desc: "Update any business fields", scope: "businesses:write" },
              { method: "GET", path: "/users", desc: "List all registered users (paginated)", scope: "users:read" },
              { method: "GET", path: "/leads", desc: "Get all leads: best-of-best interests + abandoned listings", scope: "leads:read" },
              { method: "GET", path: "/marketplace", desc: "List all marketplace items. Query: listing_type, search", scope: "marketplace:read" },
              { method: "GET", path: "/stats", desc: "Full directory analytics & counts", scope: "analytics:read" },
            ].map((ep, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${ep.method === "GET" ? "text-[#10B981] bg-[#10B981]/10" : ep.method === "PATCH" ? "text-[#F59E0B] bg-[#F59E0B]/10" : "text-[#EF4444] bg-[#EF4444]/10"}`}>
                  {ep.method}
                </span>
                <div className="flex-1">
                  <code className="text-[#E2E8F0] text-sm">{ep.path}</code>
                  <p className="text-[#64748B] text-xs mt-0.5">{ep.desc}</p>
                </div>
                <span className="text-[#7C3AED] text-xs bg-[#7C3AED]/10 px-2 py-0.5 rounded">{ep.scope}</span>
              </div>
            ))}

            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-[#94A3B8] text-sm mb-2 font-medium">Example: List all businesses</p>
              <pre className="text-[#00E5FF] text-xs font-mono whitespace-pre-wrap">{`curl -X GET "${BASE_URL}/api/v1/admin/public/businesses?page=1&limit=10" \\
  -H "X-API-Key: ahp_admin_your_key_here"`}</pre>
            </div>

            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-[#94A3B8] text-sm mb-2 font-medium">Example: Update a business</p>
              <pre className="text-[#00E5FF] text-xs font-mono whitespace-pre-wrap">{`curl -X PATCH "${BASE_URL}/api/v1/admin/public/businesses/credit-firm-inc" \\
  -H "X-API-Key: ahp_admin_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"is_featured": true, "status": "published"}'`}</pre>
            </div>

            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-[#94A3B8] text-sm mb-2 font-medium">Example: Get all leads</p>
              <pre className="text-[#00E5FF] text-xs font-mono whitespace-pre-wrap">{`curl -X GET "${BASE_URL}/api/v1/admin/public/leads" \\
  -H "X-API-Key: ahp_admin_your_key_here"`}</pre>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
